// "<Platform> Ads Clicks" horizontal bar widget — AA report-page parity
// (e.g. "Meta Ads | Ad Sets" and "Meta Ads | Campaigns"). ONE generic
// component for both levels: same AdEntityRow[] shape (see
// AdEntityBreakdownTable.tsx's docstring for the field-by-field proof),
// just a different `rows` slice (campaigns vs ad_groups) and `title`.
// Shared between the Campaigns and Ad Sets pairs on purpose — building two
// near-identical bar charts would be duplicated work AND a merge-conflict
// magnet on this shared worktree.
//
// `recharts` only, `BarChart` `layout="vertical"` — locked by the chart
// bake-off, no exceptions for a single chart.
//
// Bar shape: each row is rendered as a STACKED pair — a coral "clicks"
// segment sized to the entity's own value, plus a muted "remainder" segment
// that pads it out to the shared max — so every row's total bar width is
// identical (= the max of the visible set) and the coral portion alone
// communicates relative size, exactly like AA's own track-style bars. The
// value label is attached to the remainder segment (not the coral one) so
// it lands at the fixed right edge of every row regardless of that row's
// own value, matching the screenshot's right-aligned value column — never
// floating at the end of a short bar.
//
// Bars re-scale to the max of the FILTERED set, not the unfiltered one —
// searching down to a smaller subset re-normalizes the chart the same way
// AA's own widget does, rather than leaving mostly-empty bars against a
// stale max.
//
// Duplicate labels are real, not an edge case to shrug off: on the Ads
// level (same creative running in two different ad sets) the SAME name
// legitimately appears twice with two different click counts -- ALIBI
// Music has SIX such pairs, not one (architect-confirmed, ruling 5a) --
// e.g. "15 sec Music With Emotion - SEV2 - Copy" is 2,103 clicks in one
// ad set and 634 in another. recharts' `YAxis type="category"` positions
// and keys rows off its `dataKey`'s VALUE, not array index -- feeding it
// `name` directly would risk exactly the collapse/merge this widget must
// not do. Every row instead gets a synthetic unique `id` (entity_id, or a
// `name#index` fallback if that's ever blank) that ONLY the axis/tooltip
// plumbing sees; `tickFormatter`/the tooltip content both look the real
// `name` back up from `chartData` so what renders on screen is unaffected.
//
// That fixed collapsing, but not READABILITY: two bars both labeled "15
// sec Music With Emotion - SEV2 - Copy" are still visually identical with
// no way to tell them apart. Ruling 5a: the tooltip ALWAYS shows
// `name — parent` (parent = the ad's ad set on the Ads chart, the ad
// set's campaign on the Ad Sets chart -- same `AdEntityRow.parent` field
// either way), and the TICK LABEL appends the same qualifier only when
// `name` is duplicated within the currently-rendered set -- unconditional
// qualifiers would bloat the ~90% of ticks that are already unique to
// solve a ~10% problem, but unconditional bare names ship a chart that
// can't be read correctly for the exact client it was verified against.

import { useMemo, useState } from "react";
import { Bar, BarChart, LabelList, ResponsiveContainer, Text, Tooltip, XAxis, YAxis } from "recharts";
import type { TooltipProps } from "recharts";
import { Search, X } from "lucide-react";
import type { AdEntityRow } from "@/types/clientAnalytics";
import { formatNum } from "@/components/client-analytics/primitives";

export interface EntityClicksBarChartProps {
  title: string; // e.g. "Meta Ads Clicks"
  rows: AdEntityRow[];
  maxBars?: number; // default 5 -- matches the ALIBI Music reference (top 5 of 6 ad sets)
  searchPlaceholder?: string;
  emptyMessage?: string; // shown when `rows` itself is empty (no data at all)
}

const BAR_ROW_HEIGHT = 34;
const NAME_COLUMN_WIDTH = 168;

// The one chart in this folder with no truncation guard: recharts renders
// YAxis tick labels as raw SVG <text> -- no CSS `truncate`, no line-wrap, no
// native ellipsis -- so a long name just runs straight out of
// NAME_COLUMN_WIDTH and either overflows the plot or collides with the
// bars. Real proof: Lanracorp's ad group "Herbicide Spraying,
// Grounds/Maintenance, Operators/Foreman/Trees (IL, PA, OH)" is 77 chars,
// and qualifiedLabel()'s "name — parent" tie-breaker (ruling 5a above) can
// make it longer still. Every table elsewhere in this folder handles the
// same problem the DOM way (`max-w-[...] truncate` + a `title` attribute);
// this is the SVG-native equivalent -- measure the REAL rendered width with
// a canvas 2D context (the tick font is proportional, not monospace, so a
// fixed character-count budget would either waste space on narrow strings
// or still overflow on wide ones) and binary-search the longest prefix that
// fits alongside an ellipsis.
let measureCtx: CanvasRenderingContext2D | null | undefined;
function getMeasureCtx(): CanvasRenderingContext2D | null {
  if (measureCtx !== undefined) return measureCtx;
  const canvas = typeof document !== "undefined" ? document.createElement("canvas") : null;
  measureCtx = canvas?.getContext("2d") ?? null;
  if (measureCtx) measureCtx.font = "12px -apple-system, system-ui, sans-serif"; // matches YAxisNameTick's own fontSize
  return measureCtx;
}

const ELLIPSIS = "…";
// A few px short of the full reserved width so the truncated glyph never
// visually kisses the axis boundary / first bar.
const TICK_TEXT_MAX_WIDTH = NAME_COLUMN_WIDTH - 8;

function truncateTickLabel(label: string): string {
  const ctx = getMeasureCtx();
  // No canvas (SSR/test env) -- render the full string rather than guess at
  // a truncation point with no way to measure it.
  if (!ctx || ctx.measureText(label).width <= TICK_TEXT_MAX_WIDTH) return label;
  const ellipsisWidth = ctx.measureText(ELLIPSIS).width;
  let lo = 0;
  let hi = label.length;
  // Binary search the longest prefix whose measured width (plus the
  // ellipsis) still fits -- cheaper than trimming one character at a time
  // for a 77-char name like Lanracorp's.
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    const fits = ctx.measureText(label.slice(0, mid)).width + ellipsisWidth <= TICK_TEXT_MAX_WIDTH;
    if (fits) lo = mid;
    else hi = mid - 1;
  }
  return lo <= 0 ? ELLIPSIS : `${label.slice(0, lo)}${ELLIPSIS}`;
}

type TextAnchor = "start" | "middle" | "end" | "inherit";
type VerticalAnchor = "start" | "middle" | "end";

// Custom YAxis tick, replacing the previous plain `tick={{ fontSize, fill }}`
// style object -- that path only ever returns a plain string through
// `tickFormatter`, which is exactly the SVG <text> dead end described above.
// `labelById` isn't one of recharts' own tick props; it survives because
// recharts clones this element with its OWN computed layout props merged IN
// (x/y/payload/textAnchor/verticalAnchor -- see CartesianAxis.renderTickItem)
// rather than replacing the element's existing props outright, so passing
// extra data straight through as a prop is safe -- the same trick
// ClicksTooltip already relies on to read the full ChartRow off `payload`.
// Reuses recharts' own `Text` component (not a bare `<text>`) so
// textAnchor/verticalAnchor centering matches the built-in renderer exactly;
// only the string it's given is pre-truncated.
function YAxisNameTick({
  x,
  y,
  payload,
  textAnchor,
  verticalAnchor,
  labelById,
}: {
  x?: number;
  y?: number;
  payload?: { value: string };
  textAnchor?: TextAnchor;
  verticalAnchor?: VerticalAnchor;
  labelById: Map<string, string>;
}) {
  const id = payload?.value ?? "";
  const full = labelById.get(id) ?? id;
  const truncated = truncateTickLabel(full);
  return (
    <g>
      {/* Always present, same convention as the DOM tables' `title` attr in
          this folder -- a harmless no-op on an already-short label, the
          only way to recover the full name on a truncated one. */}
      <title>{full}</title>
      <Text
        x={x}
        y={y}
        textAnchor={textAnchor ?? "end"}
        verticalAnchor={verticalAnchor ?? "middle"}
        fontSize={12}
        fill="var(--color-foreground)"
      >
        {truncated}
      </Text>
    </g>
  );
}

interface ChartRow {
  id: string; // unique per row -- see file docstring's duplicate-label note
  name: string;
  parent: string | null; // ruling 5a qualifier -- ad set (Ads chart) or campaign (Ad Sets chart)
  clicks: number;
  remainder: number;
}

// Ruling 5a: the tooltip's name+qualifier line, shared by the tooltip
// (always qualified) and the tick label (conditionally qualified) so the
// two can never disagree about what "qualified" looks like.
function qualifiedLabel(row: Pick<ChartRow, "name" | "parent">): string {
  return row.parent ? `${row.name} — ${row.parent}` : row.name;
}

// The "remainder" segment only exists to pad every row's bar out to the
// shared max (see file docstring) — it isn't a real metric, so the tooltip
// shows just the entity name + its actual clicks, never a "remainder: 1,477"
// line a viewer would have no way to make sense of. Reads `name`/`parent`
// off the hovered datum's own `payload` (the full ChartRow), not the axis
// `label` -- `label` is the synthetic `id`, not the display name
// (duplicate-label note above), so this must not use it directly.
// ALWAYS qualified with `parent` (ruling 5a) -- unlike the tick label,
// which only qualifies duplicates, the tooltip is a single row in
// isolation and can't rely on visual proximity to disambiguate.
function ClicksTooltip({ active, payload }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload as ChartRow | undefined;
  const clicks = payload.find((p) => p.dataKey === "clicks")?.value;
  if (!row) return null;
  return (
    <div
      className="rounded-md border border-border bg-card px-2.5 py-1.5 text-dash-context shadow-sm"
      style={{ borderRadius: 8 }}
    >
      <div className="font-medium text-foreground">{qualifiedLabel(row)}</div>
      <div className="text-muted-foreground">{formatNum(typeof clicks === "number" ? clicks : null)} clicks</div>
    </div>
  );
}

export function EntityClicksBarChart({
  title,
  rows,
  maxBars = 5,
  searchPlaceholder = "Search…",
  emptyMessage = "Not enough data yet",
}: EntityClicksBarChartProps) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term ? rows.filter((r) => r.name.toLowerCase().includes(term)) : rows;
  }, [rows, search]);

  const chartData: ChartRow[] = useMemo(() => {
    const top = [...filtered].sort((a, b) => b.clicks - a.clicks).slice(0, maxBars);
    const max = Math.max(1, ...top.map((r) => r.clicks));
    return top.map((r, i) => ({
      id: r.entity_id || `${r.name}#${i}`,
      name: r.name,
      parent: r.parent ?? null,
      clicks: r.clicks,
      remainder: max - r.clicks,
    }));
  }, [filtered, maxBars]);

  // Ruling 5a: tick labels qualify ONLY names that collide within the
  // rendered set (`chartData`, i.e. post-search/post-maxBars -- a name
  // duplicated elsewhere in `rows` but not among the bars actually shown
  // doesn't need disambiguating here). Recomputed whenever chartData
  // changes so a search that narrows down to just one of a duplicate pair
  // correctly drops that pair's qualifier.
  const labelById = useMemo(() => {
    const nameCounts = new Map<string, number>();
    for (const r of chartData) nameCounts.set(r.name, (nameCounts.get(r.name) ?? 0) + 1);
    return new Map(
      chartData.map((r) => [r.id, (nameCounts.get(r.name) ?? 0) > 1 ? qualifiedLabel(r) : r.name]),
    );
  }, [chartData]);

  function closeSearch() {
    setSearchOpen(false);
    setSearch("");
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </div>
        <div className="flex items-center gap-1">
          {searchOpen && (
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && closeSearch()}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className="w-36 rounded-md border border-border bg-background px-2 py-1 text-dash-context text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          )}
          <button
            type="button"
            onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
            aria-label={searchOpen ? "Close search" : "Search"}
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            {searchOpen ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="flex items-center justify-center py-6 text-dash-context text-muted-foreground">
          {emptyMessage}
        </div>
      ) : chartData.length === 0 ? (
        <div className="flex items-center justify-center py-6 text-dash-context text-muted-foreground">
          No results match your search.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={chartData.length * BAR_ROW_HEIGHT + 16}>
          <BarChart
            data={chartData}
            layout="vertical"
            barCategoryGap="28%"
            margin={{ top: 0, right: 56, bottom: 0, left: 0 }}
          >
            <XAxis type="number" hide domain={[0, "dataMax"]} />
            <YAxis
              type="category"
              dataKey="id"
              tickFormatter={(id: string) => labelById.get(id) ?? id}
              width={NAME_COLUMN_WIDTH}
              tickLine={false}
              axisLine={false}
              tick={<YAxisNameTick labelById={labelById} />}
            />
            <Tooltip cursor={{ fill: "var(--color-muted)" }} content={<ClicksTooltip />} />
            <Bar dataKey="clicks" stackId="row" fill="var(--color-coral)" radius={[4, 0, 0, 4]} barSize={10} />
            <Bar dataKey="remainder" stackId="row" fill="var(--color-muted)" radius={[0, 4, 4, 0]} barSize={10}>
              <LabelList
                dataKey="clicks"
                position="right"
                formatter={(value: number) => formatNum(value)}
                style={{ fill: "var(--color-foreground)", fontSize: 12, fontWeight: 700 }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
